import { useEffect, useMemo, useRef, useState } from 'react';
import { useLanguage, useTranslation } from '../../i18n/LanguageContext';
import { fillSlots } from '../../i18n/fillSlots';
import { User, Settings, LogOut, Menu, Bell } from 'lucide-react';
import { LogoutDialog } from '../ui/logout-dialog';
import { useNow } from '@/features/meetings';
import { useTheme } from '../../contexts/ThemeContext';
import './animations.css';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { NotificationDrawer } from '../notifications';
import { useNotificationsStore } from '@/features/notifications';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { DEFAULT_DISPLAY_NAME, userDisplayName } from '@/features/auth/utils/display-name';
import { UserAvatar } from '@/features/settings/components/UserAvatar';
import { AUTH_ROUTES } from '@/shared/constants/routes.constants';
import { toast } from '../ui/toast-utils';

/* ─── Header Component ───────────────────────────────────────── */
interface HeaderProps {
  userName?: string
  onMenuClick?: () => void
}

interface UserMenuProps {
  userName: string
  /** Outer size in px, border included. */
  size?: number
}

/** The ring's border-2, on each side. */
const AVATAR_RING = 2

function UserMenu({ userName, size = 40 }: UserMenuProps) {
  const t = useTranslation();
  const [logoutOpen, setLogoutOpen] = useState(false)
  const navigate=useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);

  const handleLogout = async () => {
    await logout({ endSecureIdSession: true });
    toast.success({ title: t('auth.signedOut'), description: t('auth.seeYouSoon') });
    navigate(AUTH_ROUTES.LOGIN, { replace: true });
  };

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div
          style={{ width: size, height: size }}
          className="rounded-full overflow-hidden cursor-pointer shadow-lg hover:opacity-90 transition-opacity border-2 border-card/15"
        >
          {/* This used to hardcode one person's Zoho photo, so every account on
              every deployment rendered the same stranger's face. Sized to the
              space inside the ring: a 40px avatar in a 36px (32px on mobile)
              space sat in its top-left corner, initials 2-4px off centre. */}
          <UserAvatar
            name={user?.name || userName}
            avatarUrl={user?.avatarUrl}
            size={size - 2 * AVATAR_RING}
          />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 sm:w-66 bg-card dark:bg-card border border-border shadow-lg rounded-xl">
        {/* User Info Section */}
        <div className="flex items-center gap-3 px-3 py-3">
          <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 border-2 border-stroke-100 dark:border-border shadow-sm">
            <UserAvatar
              name={user?.name || userName}
              avatarUrl={user?.avatarUrl}
              size={48 - 2 * AVATAR_RING}
            />
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-foreground">{user?.name || userName}</span>
            <span className="text-xs text-slate-500 dark:text-slate-400">{user?.email}</span>
          </div>
        </div>
        
        <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
        
        <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer py-2.5 px-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg mx-1">
          <User className="w-4 h-4 mr-3 text-slate-600 dark:text-foreground" />
          <span className="text-slate-700 dark:text-slate-300">{t("nav.viewProfile")}</span>
        </DropdownMenuItem>
        
        <DropdownMenuItem onClick={() => navigate('/settings/general')} className="cursor-pointer py-2.5 px-3 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg mx-1">
          <Settings className="w-4 h-4 mr-3 text-slate-600 dark:text-foreground" />
          <span className="text-slate-700 dark:text-slate-300">{t("nav.settings")}</span>
        </DropdownMenuItem>
        
        <DropdownMenuSeparator className="bg-slate-200 dark:bg-slate-700" />
        
        <DropdownMenuItem 
          className="cursor-pointer py-2.5 px-3 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg mx-1"
          onClick={() => setLogoutOpen(true)}
        >
          <LogOut className="w-4 h-4 mr-3 text-red-500" />
          <span className="text-red-600 dark:text-red-400 font-medium">{t("nav.logout")}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <LogoutDialog
      open={logoutOpen}
      onOpenChange={setLogoutOpen}
      onConfirm={handleLogout}
    />
    </>
  )
}

export default function Header({ userName, onMenuClick }: HeaderProps) {
  // No caller passes `userName`, so this is what the whole app shows — it must
  // come from the session. A signed-in user whose stored profile is missing
  // both name and email (an incomplete record, not a guest) gets the same
  // neutral fallback every other caller of userDisplayName uses, rather than
  // a guest-flavored "there" that reads as a wrong name.
  const authUser = useAuthStore((s) => s.user)
  const displayName = userName ?? userDisplayName(authUser, DEFAULT_DISPLAY_NAME)
  const now = useNow()
  const { t, language } = useLanguage()
  const { theme } = useTheme()
  const [open, setOpen] = useState(false);
  const [blink, setBlink] = useState(true);
  const storedNotifications = useNotificationsStore((s) => s.notifications)
  const markAsRead = useNotificationsStore((s) => s.markAsRead)
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead)
  const clearAllNotifications = useNotificationsStore((s) => s.clearAll)
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null)
  const searchContainerRef = useRef<HTMLDivElement>(null)

  // The drawer expects `timestamp: Date`; the store persists `timestampMs: number`.
  // Map at the boundary so the legacy drawer keeps working unchanged.
  const notifications = useMemo(
    () =>
      storedNotifications.map((n) => ({
        id: n.id,
        type: n.type,
        title: n.title,
        message: n.message,
        timestamp: new Date(n.timestampMs),
        isRead: n.isRead,
        actionUrl: n.actionUrl,
        metadata: n.metadata,
      })),
    [storedNotifications],
  )

  // Toggle blink every second
  useEffect(() => {
    const interval = setInterval(() => {
      setBlink(prev => !prev)
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const handleMarkAsRead = (id: string) => markAsRead(id)
  const handleMarkAllAsRead = () => markAllAsRead()

  const unreadCount = notifications.filter(n => !n.isRead).length

  const getHours = (date: Date) => {
    const hour = date.getHours()
    const hour12 = hour % 12 || 12 // Convert to 12-hour format (0 becomes 12)
    return hour12.toString().padStart(2, '0')
  }

  const getMinutes = (date: Date) => {
    const minute = date.getMinutes()
    return minute.toString().padStart(2, '0')
  }

  // In the current language: 'en-US' here and below left the date and AM/PM in
  // English beside an otherwise translated (and, in Arabic, mirrored) header.
  const getAmPm = (date: Date) =>
    new Intl.DateTimeFormat(language, { hour: 'numeric', hour12: true })
      .formatToParts(date)
      .find((p) => p.type === 'dayPeriod')?.value ?? ''
  
  const formatDate = (date: Date) => {
    return date.toLocaleDateString(language, { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    })
  }
  
  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
    }
  }, [open])

  // Click outside to close search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])
  // Read off the clock at render, in the current language. It was set once in
  // English on mount, so it never followed a language switch (or the evening).
  const hour = now.getHours()
  const greeting = t(
    hour >= 5 && hour < 12 ? 'meetings.goodMorning'
      : hour >= 12 && hour < 17 ? 'meetings.goodAfternoon'
      : 'meetings.goodEvening',
  )
  function AnimatedHelloEmoji() {
  return (
    <span className="inline-block text-2xl ms-2" style={{ animation: 'wave 2s ease-in-out infinite' }}>
      👋
    </span>
  )
}
  return (
    <div className="w-full bg-gradient-to-l from-[#F4F5F6] via-primary/5 to-transparent dark:bg-gradient-to-l dark:from-white/5 dark:via-white/5 dark:to-transparent shadow-sm">
      {/* Mobile / Tablet Header */}
      <div className="flex lg:hidden items-center justify-between px-3 pt-2 pb-3 border-b border-border/40 relative">
        <button
          type="button"
          aria-label={t('common.openMenu')}
          onClick={onMenuClick}
          className="w-9 h-9 rounded-full bg-card flex items-center justify-center border border-border text-foreground hover:bg-muted transition-colors"
        >
          <Menu className="w-5 h-5" />
        </button>
        <img 
          src={theme === 'dark' ? '/InmeetWhiteLogo.svg' : '/Inmeet.svg'} 
          alt="InMeet" 
          className="h-8" 
        />
        <div className="flex items-center gap-2">
          {/* Mobile Notification Button */}
          <div className="relative">
            <button
              onClick={() => setNotificationDrawerOpen(true)}
              className="w-9 h-9 rounded-full bg-card flex items-center justify-center border border-border text-foreground hover:bg-muted transition-colors"
            >
              <Bell className="w-4 h-4" />
            </button>
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-4 w-4 flex items-center justify-center p-0 text-[10px] animate-bounce"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </div>
          <UserMenu userName={displayName} size={36} />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden lg:flex items-center justify-between px-3 sm:px-4 pt-2 sm:pt-1 pb-3 sm:pb-1 animate-fade-in" style={{ animation: "fadeIn 0.4s ease both" }}>
        <div className="flex items-start flex-col justify-start min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {/* <h1 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">
              {getGreeting()}, {userName}
            </h1> */}
             <p className="text-[17px] sm:text-md text-slate-600 dark:text-slate-300 truncate">
          {/* <bdi> keeps a Latin name from swapping places with the punctuation
              around it in a right-to-left sentence. */}
          {fillSlots(t('dashboard.greeting', { greeting }), {
            name: <bdi className="font-semibold">{displayName}</bdi>,
          })} <span className="hidden sm:inline">{t('dashboard.productiveDay')}</span><span className="hidden sm:inline"><AnimatedHelloEmoji /></span>
        </p>
          </div>
          {/* <p className="text-sm text-muted-foreground truncate">
            {formatDate(now)}
          </p> */}
        </div>
        
        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Search Container */}
          <div ref={searchContainerRef} className="hidden lg:flex items-center">
            <input
              ref={inputRef}
              type="text"
              placeholder={`${t('common.search')}…`}
              className={`
                h-9 w-56
                rounded-lg
                text-sm
                px-3
                bg-muted/50 dark:bg-card
                border border-border/50
                outline-none
                transition-all duration-200 ease-out
                focus:bg-background dark:focus:bg-slate-700
                focus:border-primary/50
                ${open ? "opacity-100 mr-2" : "opacity-0 w-0 px-0 mr-0 pointer-events-none"}
              `}
            />

            {/* <button
              onClick={() => setOpen(!open)}
              className="w-9 h-9 flex items-center justify-center rounded-lg transition-all"
            >
              <Search className="w-4 h-4 text-muted-foreground" />
            </button> */}
          </div>
          
          {/* Date and Time Display */}
          <div className="hidden md:flex items-center gap-3 px-3 py-2">
            <div className="text-right">
              <div className="text-[20px] font-bold text-primary dark:text-foreground">
                {getHours(now)}<span style={{ opacity: blink ? 1 : 0 }}>:</span>{getMinutes(now)} {getAmPm(now)}
              </div>
              <div className="text-[12px] text-muted-foreground">
                {formatDate(now)}
              </div>
            </div>
          </div>
          
          {/* Notification Button */}
          <div className="relative">
            <button
              id="tour-header-notification"
              onClick={() => setNotificationDrawerOpen(true)}
              className="w-9 h-9 flex items-center justify-center rounded-lg transition-all hover:bg-muted"
            >
              <Bell className="w-5 h-5 text-muted-foreground" />
            </button>
            {unreadCount > 0 && (
              <Badge 
                variant="destructive" 
                className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs animate-bounce"
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </Badge>
            )}
          </div>

          <div id="tour-header-avatar">
            <UserMenu userName={displayName} />
          </div>
        </div>
      </div>

      {/* Notification Drawer */}
      <NotificationDrawer
        isOpen={notificationDrawerOpen}
        onClose={() => setNotificationDrawerOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onMarkAllAsRead={handleMarkAllAsRead}
        onClearAll={clearAllNotifications}
      />
    </div>
  )
}
